import { SuccessClientResponse } from '@orbis-systems/orbis-api-clients';
import { Account, GroupRtbs, PositionType, Rtbs, User } from '@orbis-systems/orbis-api-middleware';
import { queryOptions } from '@tanstack/react-query';
import { orbis } from '../api';
import { PortfolioWithExtraInfo } from '@/types';
import { buildBranchPortfolioWithBreakdownFromCache } from './branch-query-utils';
import { selectGlobalSettings, store } from '@/redux';
import { isMidasQuoteSource } from '@/utils';
import { enrichPortfolioWithMidasQuotes } from '../quote/utils';

export type BreakdownBranchPortfolioQueryParams = {
    lastUpdatedAt: number;
    currency: string;
};
export interface BranchPortfolioQueryParams {
    quotesStaleTime?: number;
    combined?: boolean;
    currency?: string;
    symbol?: string;
    positionType?: PositionType;
    enabled?: boolean;
    staleTime?: number;
}

export type BranchRtbsResponse = SuccessClientResponse<GroupRtbs[]>;
export type BranchInventoryResponse = SuccessClientResponse<Account[]>;
export type BranchRtbResponse = SuccessClientResponse<Rtbs>;
export type BranchPortfolioResponse = SuccessClientResponse<PortfolioWithExtraInfo>;
export type BranchUsersListResponse = SuccessClientResponse<User[]>;

export const branchQueries = {
    all: () =>
        queryOptions({
            queryKey: ['branch'],
        }),
    branchRtbs: () =>
        queryOptions<BranchRtbsResponse>({
            queryKey: [...branchQueries.all().queryKey, 'branchRtbs'],
            queryFn: () => orbis.branch.branchRtbs(),
        }),
    branchInventory: () =>
        queryOptions<BranchInventoryResponse>({
            queryKey: [...branchQueries.all().queryKey, 'branchInventory'],
            queryFn: () => orbis.branch.branchInventory(),
        }),
    branchRtb: () =>
        queryOptions<BranchRtbResponse>({
            queryKey: [...branchQueries.all().queryKey, 'branchRtb'],
            queryFn: () => orbis.branch.branchRtb(),
        }),
    branchPortfolio: (params: BranchPortfolioQueryParams) => {
        const { quotesStaleTime, ...restParams } = params;
        return queryOptions<BranchPortfolioResponse>({
            queryKey: [...branchQueries.all().queryKey, 'branchPortfolio', restParams],
            queryFn: async () => {
                const forceDirectMidasQuotes = selectGlobalSettings(store.getState()).forceDirectMidasQuotes;
                const portfolioPromise = orbis.branch.branchPortfolio({
                    // @ts-expect-error: // FIXME: missing query param (currency). Waiting for backend update
                    currency: restParams.currency,
                    combined: restParams.combined,
                    loadQuotes: forceDirectMidasQuotes && isMidasQuoteSource() ? false : true,
                    loadIndustries: true,
                    loadRtb: true,
                    marketCapBreakdown: true,
                });
                if (forceDirectMidasQuotes && isMidasQuoteSource()) {
                    const portfolioResponse = await portfolioPromise;
                    const enrichedPortfolio = await enrichPortfolioWithMidasQuotes({
                        portfolio: portfolioResponse.data,
                        staleTime: quotesStaleTime,
                    });
                    return { ...portfolioResponse, data: enrichedPortfolio };
                } else return portfolioPromise;
            },
        });
    },
    branchPortfolioWithBreakdown: (params: BreakdownBranchPortfolioQueryParams) =>
        queryOptions({
            queryKey: [...branchQueries.all().queryKey, 'branchPortfolioWithBreakdown', params],
            queryFn: buildBranchPortfolioWithBreakdownFromCache({ currency: params.currency }),
        }),
    usersList: () =>
        queryOptions<BranchUsersListResponse>({
            queryKey: [...branchQueries.all().queryKey, 'usersList'],
            queryFn: () => orbis.advisory.userAccountManagement.list(),
        }),
} as const;
